import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Actions
import {
  clearAllTemplates,
  getAllProducts,
  getAllTemplates,
  searchAndAdvanceChange,
} from '~/redux/actions/template-builder';

//Utils
import { sortOrderForTemplates } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { buildTemplateQueryParams, parseTemplateQueryParams } from '~/utils/templateQueryParams';
import { getAvailableDesignerCredits, getDesignCreditsLabel } from '~/utils/design-credits';

// Components
import TemplateListing from './TemplateListing/TemplateListing';
import FilterModal from './FilterModal';
import NoTemplateScreen from './NoTemplateScreen';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';

// Icons
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import WindowOutlinedIcon from '@mui/icons-material/WindowOutlined';

// MUI Components
import { Container, Box, Button, Typography, useMediaQuery } from '@mui/material';

// Styles
import './styles.scss';
import GridView from './GridView';

const EMPTY_PRODUCT_IDS = [];
const EMPTY_SELECTED_PRODUCT_IDS = [];

/**
 * Renders a search input, a filter button, a create button, and a template listing component.
 * Handles navigation to the template builder page and manages the state of the search input.
 * Search and filter state is persisted in the URL as query params so that reloads,
 * copy-pasted URLs, and browser back/forward all restore the same view.
 *
 * @returns {JSX.Element} The rendered Templates component.
 */
const Templates = () => {
  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(null);
  const [isOpenAdvanceFilter, setIsOpenAdvanceFilter] = useState(false);
  const [searchApplied, setSearchApplied] = useState(false);
  const [changeViewMode, setChangeViewMode] = useState('List');
  const [hasResolvedProductsRequest, setHasResolvedProductsRequest] = useState(false);
  const [isTemplateUrlRequestPending, setIsTemplateUrlRequestPending] = useState(true);
  const handledTemplateQueryRef = useRef(null);
  const templates = useSelector((state) => state.templateReducer.templates);
  const templatesPerPageRef = useRef(templates.perPage);
  // Latest list sort (kept in Redux by the listing). A ref, so a sort change doesn't re-run the URL effect.
  const sortBy = useSelector((state) => state.templateReducer.sortBy);
  const sortOrder = useSelector((state) => state.templateReducer.sortOrder);
  const sortRef = useRef({ sortBy, sortOrder });
  sortRef.current = { sortBy, sortOrder };
  const templatesPagination = useSelector((state) => state.templateReducer.templatesPagination);
  const totalTemplatesInDB = useSelector((state) => state.templateReducer.totalRecordsInDB);

  const search = useSelector((state) => state.templateReducer.search);
  const userData = useSelector((state) => state.userReducers.user);
  const currentMode = useSelector((state) =>
    state.userReducers.user.isLiveMode === true
      ? true
      : state.userReducers.user.isLiveMode === false
        ? false
        : null
  );
  const searchProductIds = useSelector((state) => state.templateReducer.searchProductIds);
  const searchCreator = useSelector((state) => state.templateReducer.searchCreator);
  const products = useSelector((state) => state.templateReducer.products);

  const updatedProducts = useMemo(
    () =>
      products
        .map((product) => {
          if (product.productType === 'Postcards') {
            return product.size.map((size) => ({
              id: size.id,
              title: `${product.productType} - ${size.size}`,
            }));
          } else if (product.productType === 'Professional Letters') {
            const windowedLabel = product.windowed ? 'Windowed' : 'Non-Windowed';
            return {
              id: product.id,
              title: `${product.productType} - ${windowedLabel}`,
            };
          } else {
            return {
              id: product.id,
              title: product.productType,
            };
          }
        })
        .flat()
        .sort((a, b) => {
          const indexA = sortOrderForTemplates.indexOf(a.title);
          const indexB = sortOrderForTemplates.indexOf(b.title);
          return indexA - indexB;
        }),
    [products]
  );
  const templateQueryString = searchParams.toString();
  const designCreditsAvailable = useMemo(() => getAvailableDesignerCredits(userData), [userData]);

  // Handle navigation to template builder page
  const handleCreate = useCallback(() => navigate('/create-template'), [navigate]);
  // const handleCreateHtmlTemplate = useCallback(
  //   () => navigate('/html-template-builder'),
  //   [navigate]
  // );
  const handleOpenHireDesigner = useCallback(() => navigate('/hire-a-designer'), [navigate]);

  const updateTemplateUrl = useCallback(
    (queryState) => {
      const nextSearchParams = new URLSearchParams();
      // Callers that don't pass `sort` keep the sort already in the URL.
      const currentSort = parseTemplateQueryParams(new URLSearchParams(templateQueryString)).sort;
      const nextQueryParams = buildTemplateQueryParams({ sort: currentSort, ...queryState });

      Object.entries(nextQueryParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => nextSearchParams.append(key, item));
          return;
        }
        nextSearchParams.set(key, value);
      });

      if (nextSearchParams.toString() === templateQueryString) {
        return false;
      }

      setIsTemplateUrlRequestPending(true);
      setSearchParams(nextSearchParams, { replace: true });
      return true;
    },
    [templateQueryString, setSearchParams]
  );

  const handleSearchChange = (event) => {
    const nextSearch = event.target.value.trimStart();
    dispatch(searchAndAdvanceChange(event.target.name, nextSearch));

    // Match the other listing pages: once an applied search is erased with
    // Backspace/Delete, immediately restore the unsearched result set.
    if (!nextSearch && searchApplied) {
      updateTemplateUrl({ search: '', searchProductIds, searchCreator });
      setSearchApplied(false);
    }
  };

  const clearAdvanceFilter = () => {
    setSelectedTemplateIds(null);
    const didUpdateUrl = updateTemplateUrl({ search, searchProductIds: '', searchCreator: '' });

    if (!didUpdateUrl) {
      dispatch(searchAndAdvanceChange('searchProductIds', ''));
      dispatch(searchAndAdvanceChange('searchCreator', ''));
    }
  };

  // Sort lives in the URL (like search and filters); the URL effect below refetches from page 1.
  const handleSortChange = (nextSort) => {
    updateTemplateUrl({
      search: urlSearch,
      searchProductIds: urlProductIds,
      searchCreator: urlCreator,
      sort: nextSort,
    });
  };

  const handleApplyFilter = ({ productsIds, selectedIds, createdText }) => {
    setSelectedTemplateIds(selectedIds);
    updateTemplateUrl({
      search,
      searchProductIds: productsIds,
      searchCreator: createdText,
    });
  };

  const filterSize = [searchProductIds, searchCreator].filter((item) => item).length;
  const parsedTemplateQuery = useMemo(
    () => parseTemplateQueryParams(new URLSearchParams(templateQueryString)),
    [templateQueryString]
  );
  const {
    search: urlSearch,
    searchProductIds: urlProductIds,
    searchCreator: urlCreator,
    sort: urlSort,
  } = parsedTemplateQuery;
  const selectedProductIdsFromQuery = useMemo(() => {
    if (!urlProductIds) {
      return EMPTY_PRODUCT_IDS;
    }

    if (updatedProducts.length === 0 && !hasResolvedProductsRequest) {
      return null;
    }

    const productTitles = urlProductIds.split(',').filter(Boolean);
    const productIds = updatedProducts
      .filter((product) => productTitles.includes(product.title))
      .map((product) => Number(product.id));

    return productIds.length > 0 ? JSON.stringify(productIds) : EMPTY_SELECTED_PRODUCT_IDS;
  }, [hasResolvedProductsRequest, urlProductIds, updatedProducts]);
  const isResolvingProductFilter = Boolean(urlProductIds && selectedProductIdsFromQuery === null);
  const templateRequestKey = `${templateQueryString}|${selectedProductIdsFromQuery ?? 'pending'}`;
  const isTemplateRouteLoading =
    isTemplateUrlRequestPending ||
    handledTemplateQueryRef.current !== templateRequestKey ||
    isResolvingProductFilter;
  const isTemplateDataLoading = isTemplateRouteLoading || templatesPagination.loading;

  const fetchTemplatesForCurrentQuery = useCallback(() => {
    dispatch(
      getAllTemplates(
        1,
        templatesPerPageRef.current,
        urlSearch,
        urlProductIds,
        urlCreator,
        '',
        selectedProductIdsFromQuery,
        true,
        false,
        true,
        urlSort.sortBy,
        urlSort.sortOrder
      )
    );
    handledTemplateQueryRef.current = templateRequestKey;
  }, [
    dispatch,
    selectedProductIdsFromQuery,
    templateRequestKey,
    urlCreator,
    urlProductIds,
    urlSearch,
    urlSort.sortBy,
    urlSort.sortOrder,
  ]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const didUpdateUrl = updateTemplateUrl({ search, searchProductIds, searchCreator });

      if (!didUpdateUrl) {
        if (isResolvingProductFilter) {
          setIsTemplateUrlRequestPending(true);
          return;
        }

        fetchTemplatesForCurrentQuery();
      }
    }
  };

  // ─── URL sync ────────────────────────────────────────────────────────────────

  /**
   * URL is the source of truth for submitted search/filter state.
   * Route hits, reloads, browser back/forward, search submit, and filter apply
   * all arrive here and produce one templates API call.
   */
  useEffect(() => {
    dispatch(searchAndAdvanceChange('search', urlSearch));
    dispatch(searchAndAdvanceChange('searchProductIds', urlProductIds));
    dispatch(searchAndAdvanceChange('searchCreator', urlCreator));
    dispatch(searchAndAdvanceChange('sortBy', urlSort.sortBy ?? ''));
    dispatch(searchAndAdvanceChange('sortOrder', urlSort.sortOrder ?? ''));
    setSearchApplied(Boolean(urlSearch));

    if (urlProductIds && selectedProductIdsFromQuery === null) {
      setIsTemplateUrlRequestPending(true);
      return;
    }

    if (handledTemplateQueryRef.current === templateRequestKey) {
      setIsTemplateUrlRequestPending(false);
      return;
    }

    fetchTemplatesForCurrentQuery();
    setIsTemplateUrlRequestPending(false);
  }, [
    dispatch,
    fetchTemplatesForCurrentQuery,
    selectedProductIdsFromQuery,
    templateRequestKey,
    urlCreator,
    urlProductIds,
    urlSearch,
    urlSort.sortBy,
    urlSort.sortOrder,
  ]);

  useEffect(() => {
    templatesPerPageRef.current = templates.perPage;
  }, [templates.perPage]);

  useEffect(() => {
    let isMounted = true;

    Promise.resolve(dispatch(getAllProducts())).finally(() => {
      if (isMounted) {
        setHasResolvedProductsRequest(true);
      }
    });

    return () => {
      isMounted = false;
      dispatch(clearAllTemplates());
      dispatch(searchAndAdvanceChange('search', ''));
      dispatch(searchAndAdvanceChange('searchProductIds', ''));
      dispatch(searchAndAdvanceChange('searchCreator', ''));
      dispatch(searchAndAdvanceChange('sortBy', ''));
      dispatch(searchAndAdvanceChange('sortOrder', ''));
    };
  }, [dispatch]);

  /**
   * When products load and a productType filter is in Redux (hydrated from URL),
   * derive the numeric selectedTemplateIds so the filter modal shows checked items.
   * This does NOT trigger another API call — it only sets local state used by the modal.
   */
  useEffect(() => {
    if (!searchProductIds) {
      setSelectedTemplateIds(null);
      return;
    }

    if (updatedProducts.length === 0) return;

    const titles = searchProductIds.split(',').filter(Boolean);
    const ids = JSON.stringify(
      updatedProducts.filter((p) => titles.includes(p.title)).map((p) => Number(p.id))
    );
    setSelectedTemplateIds(ids);
  }, [updatedProducts, searchProductIds]);

  // Clear filters only when the user actually switches Live/Test mode.
  // On reload the user mode can hydrate from null to true/false; that must not
  // wipe query params restored from the URL.
  const previousMode = useRef(null);
  useEffect(() => {
    if (currentMode === null) {
      return;
    }

    if (previousMode.current === null || previousMode.current === currentMode) {
      previousMode.current = currentMode;
      return;
    }

    previousMode.current = currentMode;
    const didUpdateUrl = updateTemplateUrl({ search: '', searchProductIds: '', searchCreator: '' });
    setSelectedTemplateIds(null);

    if (!didUpdateUrl) {
      dispatch(searchAndAdvanceChange('search', ''));
      dispatch(searchAndAdvanceChange('searchProductIds', ''));
      dispatch(searchAndAdvanceChange('searchCreator', ''));
      dispatch(
        getAllTemplates(
          1,
          templates.perPage,
          '',
          '',
          '',
          '',
          [],
          true,
          false,
          true,
          sortRef.current.sortBy,
          sortRef.current.sortOrder
        )
      );
    }
  }, [
    currentMode,
    dispatch,
    search,
    searchCreator,
    searchProductIds,
    templates.perPage,
    updateTemplateUrl,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────

  const noTemplatesInDb = () => {
    return (
      <Container maxWidth={maxWidth} className="olcEmptyStatePageContainer">
        <NoTemplateScreen />
      </Container>
    );
  };

  if (!isTemplateDataLoading && totalTemplatesInDB < 1 && totalTemplatesInDB !== null) {
    return noTemplatesInDb();
  }

  return (
    <Container maxWidth={maxWidth} className="templatesPageContainer olcPrimaryListPage">
      {isOpenAdvanceFilter && (
        <FilterModal
          open={isOpenAdvanceFilter}
          handleClose={() => setIsOpenAdvanceFilter(false)}
          updatedProducts={updatedProducts}
          setSelectedTemplateIds={setSelectedTemplateIds}
          handleApplyFilter={handleApplyFilter}
          handleClearFilter={clearAdvanceFilter}
        />
      )}
      <Box className="templatesToolbar">
        <Box className="viewModeWrapper templatesViewModeWrapper">
          <Box
            className={`viewModeItem ${changeViewMode === 'List' && 'active'}`}
            onClick={() => setChangeViewMode('List')}
            role="button"
            aria-label="List view"
            tabIndex={0}
          >
            <ViewStreamOutlinedIcon />
          </Box>
          <Box
            className={`viewModeItem ${changeViewMode === 'Grid' && 'active'}`}
            onClick={() => setChangeViewMode('Grid')}
            role="button"
            aria-label="Grid view"
            tabIndex={0}
          >
            <WindowOutlinedIcon />
          </Box>
        </Box>

        <OlcSearchInput
          searchApplied={searchApplied}
          type="text"
          autoComplete="off"
          value={search}
          name="search"
          onKeyDown={handleKeyDown}
          onChange={handleSearchChange}
          placeholder={MESSAGES.TEMPLATE.SEARCH_PLACE_HOLDER}
          title={MESSAGES.TEMPLATE.SEARCH_PLACE_HOLDER}
          onClear={() => {
            dispatch(searchAndAdvanceChange('search', ''));
            updateTemplateUrl({ search: '', searchProductIds, searchCreator });
            setSearchApplied(false);
          }}
        />

        <Box className="filterBtnWrapper">
          <OlcButton
            variant="secondary"
            onClick={() => {
              setIsOpenAdvanceFilter(true);
            }}
          >
            Filters
          </OlcButton>
          {filterSize > 0 && <Typography className="filtersCounter">{filterSize}</Typography>}
        </Box>

        {filterSize > 0 && (
          <OlcButton variant="secondary" onClick={clearAdvanceFilter} className="clearFilterBtn">
            Clear Filters
          </OlcButton>
        )}

        <Box className="templatesActionsBtnWrapper">
          <OlcButton
            variant="secondary"
            onClick={handleOpenHireDesigner}
            aria-label={getDesignCreditsLabel(designCreditsAvailable)}
          >
            <span className="designCreditsFull">
              {getDesignCreditsLabel(designCreditsAvailable)}
            </span>
            <span className="designCreditsCompact" aria-hidden="true">
              {`${designCreditsAvailable} ${designCreditsAvailable === 1 ? 'Credit' : 'Credits'} Available`}
            </span>
          </OlcButton>
          {/* <OlcButton variant="secondary" onClick={handleCreateHtmlTemplate}>
            Create HTML Template
          </OlcButton> */}
          <OlcButton variant="primary" onClick={handleCreate}>
            Create
          </OlcButton>
        </Box>
      </Box>

      {/* Template Listing Component */}
      <Box
        className={`templatesContentCard olcPrimaryListContent ${
          changeViewMode === 'Grid' ? 'templatesGridCard' : 'templatesListCard'
        }`}
      >
        {changeViewMode === 'Grid' ? (
          <GridView
            selectedTemplateIds={selectedTemplateIds}
            templates={templates}
            searchApplied={searchApplied}
            filterSize={filterSize}
            totalTemplatesInDB={totalTemplatesInDB}
            isLoading={isTemplateDataLoading}
          />
        ) : (
          <TemplateListing
            selectedTemplateIds={selectedTemplateIds}
            searchApplied={searchApplied}
            filterSize={filterSize}
            totalTemplatesInDB={totalTemplatesInDB}
            isLoading={isTemplateDataLoading}
            onSortChange={handleSortChange}
          />
        )}
      </Box>
    </Container>
  );
};

export default Templates;
